/**
 * The homeowner's page, behind the QR code on the board (#/home/<share token>): which breaker
 * feeds which rooms, under which RCD, and what to do when an RCD trips. Read from the project's
 * read-only share link; the full drawings are one tap away (#/view/<token>). Plain words, large
 * type, works on a phone.
 */
import { useEffect, useState } from 'react';
import { CloudClient } from '@elec/sync';
import type { Project } from '@elec/domain';
import { homeGuide, type HomeBoard, type HomeCircuit } from '@elec/rules';
import { LOCALES, pick, type Locale, type Translate } from '@elec/i18n';
import { ROOM_TYPES } from '@elec/symbols';

export function HomeView(props: {
  token: string;
  baseUrl: string | undefined;
  t: Translate;
  locale: Locale;
  setLocale(l: Locale): void;
}) {
  const { t, locale } = props;
  const [state, setState] = useState<{ project: Project; boards: HomeBoard[] } | 'loading' | 'error'>(
    'loading',
  );
  useEffect(() => {
    if (!props.baseUrl) return setState('error');
    new CloudClient(props.baseUrl)
      .shared(props.token)
      .then((r) => setState({ project: r.project, boards: homeGuide(r.project) }))
      .catch(() => setState('error'));
  }, [props.token, props.baseUrl]);
  const room = (r: string) => {
    const type = ROOM_TYPES.find((x) => x.value === r);
    return type ? pick(type.label, locale) : r;
  };
  const what = (c: HomeCircuit) => {
    const parts: string[] = [];
    if (c.points.socket) parts.push(t('home.sockets', { n: c.points.socket }));
    if (c.points.light) parts.push(t('home.lights', { n: c.points.light }));
    if (c.points.appliance) parts.push(t('home.appliances', { n: c.points.appliance }));
    return parts.join(' · ');
  };
  if (state === 'loading')
    return (
      <main className="home-view">
        <p className="muted">{t('home.loading')}</p>
      </main>
    );
  if (state === 'error')
    return (
      <main className="home-view">
        <h1>{t('home.title')}</h1>
        <p>{t('home.error')}</p>
      </main>
    );
  const m = state.project.meta;
  return (
    <main className="home-view" data-testid="home-view">
      <header>
        <h1>{t('home.title')}</h1>
        <p className="muted">
          {[m.title, m.installationAddress || m.ownerAddress].filter(Boolean).join(' · ')}
        </p>
        <select
          value={locale}
          onChange={(e) => props.setLocale(e.target.value as Locale)}
          aria-label={t('menu.language')}
        >
          {Object.entries(LOCALES).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
      </header>

      {state.boards.map((b, bi) => (
        <section key={bi} className="home-board">
          {state.boards.length > 1 && <h2>{b.name}</h2>}
          {b.groups.map((g, gi) => (
            <div key={gi} className="home-group" data-testid="home-group">
              <h3>
                {g.rcd
                  ? t('home.underRcd', { tag: g.rcd.tag, ma: g.rcd.sensitivityMa ?? '?' })
                  : t('home.noRcd')}
              </h3>
              <ul>
                {g.circuits.map((c) => (
                  <li key={c.code} data-testid={`home-circuit-${c.code}`}>
                    <span className="home-code">{c.code}</span>
                    <div>
                      <strong>{c.label || what(c) || t('home.circuit')}</strong>
                      {c.rooms.length > 0 && <div>{c.rooms.map(room).join(', ')}</div>}
                      <div className="muted small">
                        {[c.protection, c.label ? what(c) : ''].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      ))}

      <section className="home-help" data-testid="home-help">
        <h2>{t('home.tripTitle')}</h2>
        <ol>
          <li>{t('home.trip1')}</li>
          <li>{t('home.trip2')}</li>
          <li>{t('home.trip3')}</li>
          <li>{t('home.trip4')}</li>
          <li>{t('home.trip5')}</li>
        </ol>
        <p>{t('home.test')}</p>
        <p className="home-warn">{t('home.never')}</p>
      </section>

      <footer>
        {(m.company || m.author) && (
          <p>{t('home.installer', { name: [m.company, m.author].filter(Boolean).join(' — ') })}</p>
        )}
        <a
          href={`#/view/${props.token}`}
          onClick={(e) => {
            // The drawings open in the read-only viewer, which loads when the app starts.
            e.preventDefault();
            location.hash = `#/view/${props.token}`;
            location.reload();
          }}
          data-testid="home-drawings"
        >
          {t('home.drawings')}
        </a>
      </footer>
    </main>
  );
}
